import { useState, useMemo } from 'react'
import {
  format,
  addDays,
  subDays,
  startOfDay,
  isSameDay,
  isSameMonth,
  getISOWeek,
  getYear,
  getMonth,
  startOfMonth,
} from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const NUM_VISIBLE = 14

type StripItem =
  | { type: 'sep'; month: string; key: string }
  | { type: 'day'; date: Date; idx: number; key: string }

interface Props {
  selectedDate: Date
  onSelect: (d: Date) => void
}

export function DateStrip({ selectedDate, onSelect }: Props) {
  const [viewStart, setViewStart] = useState<Date>(() =>
    // Place selected day ~6 from the left so there are days on both sides
    startOfDay(subDays(selectedDate, 6))
  )

  const today = useMemo(() => startOfDay(new Date()), [])

  const days = useMemo(
    () => Array.from({ length: NUM_VISIBLE }, (_, i) => startOfDay(addDays(viewStart, i))),
    [viewStart]
  )

  // Drive the year/month header from the midpoint of the visible strip
  const midDay = days[Math.floor(NUM_VISIBLE / 2)]
  const viewYear = getYear(midDay)
  const viewMonth = getMonth(midDay)

  function jumpToMonth(year: number, month: number) {
    setViewStart(startOfMonth(new Date(year, month, 1)))
  }

  // Build items: insert a month-separator element whenever the month changes
  const stripItems = useMemo<StripItem[]>(() => {
    const items: StripItem[] = []
    for (let i = 0; i < days.length; i++) {
      const d = days[i]
      if (i > 0 && !isSameMonth(d, days[i - 1])) {
        items.push({ type: 'sep', month: format(d, 'MMM'), key: `sep-${d.toISOString()}` })
      }
      items.push({ type: 'day', date: d, idx: i, key: d.toISOString() })
    }
    return items
  }, [days])

  return (
    <div className="border-b bg-background shrink-0 px-3 pt-2 pb-2 space-y-1.5">
      {/* ── Year nav + month chips ── */}
      <div className="flex items-center gap-2">
        {/* Year selector */}
        <div className="flex items-center gap-0.5 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={() => jumpToMonth(viewYear - 1, viewMonth)}
          >
            <ChevronLeft className="h-3 w-3" />
          </Button>
          <span className="text-xs font-semibold tabular-nums w-9 text-center select-none">
            {viewYear}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={() => jumpToMonth(viewYear + 1, viewMonth)}
          >
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>

        {/* Month chips */}
        <div className="flex gap-0.5 flex-wrap">
          {MONTHS.map((m, i) => (
            <button
              key={m}
              onClick={() => jumpToMonth(viewYear, i)}
              className={cn(
                'px-1.5 py-0.5 rounded text-[11px] leading-none transition-colors select-none',
                i === viewMonth
                  ? 'bg-primary text-primary-foreground font-semibold'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* ── Day strip ── */}
      <div className="flex items-end gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-5 shrink-0"
          onClick={() => setViewStart((d) => subDays(d, 7))}
        >
          <ChevronLeft className="h-3 w-3" />
        </Button>

        <div className="flex flex-1 items-end gap-0.5 min-w-0">
          {stripItems.map((item) => {
            if (item.type === 'sep') {
              return (
                <div
                  key={item.key}
                  className="flex flex-col items-center self-stretch shrink-0 gap-0.5 w-4"
                >
                  <span className="text-[8px] font-bold text-primary bg-primary/10 rounded px-0.5 leading-tight">
                    {item.month}
                  </span>
                  <div className="flex-1 w-px bg-primary/30" />
                </div>
              )
            }

            const { date: d, idx } = item
            const isSelected = isSameDay(d, selectedDate)
            const isToday = isSameDay(d, today)
            const isWeekend = d.getDay() === 0 || d.getDay() === 6
            const showWeekNum = d.getDay() === 1 || idx === 0

            return (
              <div key={item.key} className="flex-1 flex flex-col items-center gap-0.5 min-w-0">
                {/* Week number label — only shown at Monday or strip start */}
                <span className="text-[9px] text-muted-foreground h-3 leading-3 select-none">
                  {showWeekNum ? `W${getISOWeek(d)}` : ''}
                </span>

                {/* Tiny calendar card */}
                <button
                  onClick={() => onSelect(d)}
                  className={cn(
                    'w-full overflow-hidden rounded border transition-all cursor-pointer',
                    isSelected
                      ? 'border-primary ring-1 ring-primary shadow-sm'
                      : isToday
                      ? 'border-blue-400'
                      : 'border-border hover:border-primary/40 hover:shadow-sm'
                  )}
                >
                  {/* Coloured top strip */}
                  <div
                    className={cn(
                      'h-[3px] w-full',
                      isSelected
                        ? 'bg-primary'
                        : isToday
                        ? 'bg-blue-400'
                        : isWeekend
                        ? 'bg-slate-300 dark:bg-slate-600'
                        : 'bg-slate-200 dark:bg-slate-700'
                    )}
                  />
                  {/* Day content */}
                  <div
                    className={cn(
                      'px-0.5 py-0.5 text-center',
                      isSelected && 'bg-primary/5'
                    )}
                  >
                    <div className="text-[9px] uppercase leading-tight text-muted-foreground select-none">
                      {format(d, 'EEE').slice(0, 2)}
                    </div>
                    <div
                      className={cn(
                        'text-xs leading-tight select-none',
                        isSelected
                          ? 'font-bold text-primary'
                          : isToday
                          ? 'font-semibold text-blue-600 dark:text-blue-400'
                          : isWeekend
                          ? 'text-muted-foreground'
                          : 'text-foreground'
                      )}
                    >
                      {format(d, 'd')}
                    </div>
                  </div>
                </button>
              </div>
            )
          })}
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-5 shrink-0"
          onClick={() => setViewStart((d) => addDays(d, 7))}
        >
          <ChevronRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  )
}
